import { mergeEvents } from '@aj/core';
import type { AppEvent } from '@aj/core';
import { useEffect, useState } from 'react';

import { allEvents, subscribeToLog } from './db.js';

export type EventLog = {
  /** `undefined` while the first read is still in flight. */
  events: AppEvent[] | undefined;
  /** Set if storage could not be opened or read. */
  error: Error | null;
};

/**
 * The event log, live.
 *
 * Reads the log from IndexedDB once, then keeps it current by applying the
 * deltas `db.appendEvents` publishes. Nothing re-reads storage on a write —
 * we are the only writer, so we already know exactly what changed.
 *
 * A failure here MUST surface. `events` stays `undefined` on error, and the
 * screen gates on it, so swallowing the rejection would leave the app blank
 * forever with nothing but a console line to explain it.
 */
export function useEventLog(): EventLog {
  const [events, setEvents] = useState<AppEvent[] | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loaded = false;
    /** A reset that lands mid-load makes the in-flight read stale. */
    let discardInitial = false;
    /** Writes that land before the initial read resolves. Dropping these
     *  would lose a tap made during startup. */
    let buffered: AppEvent[] = [];

    const unsubscribe = subscribeToLog((change) => {
      if (cancelled) return;

      if (change.kind === 'reset') {
        buffered = [];
        loaded = true;
        discardInitial = true;
        setEvents([]);
        return;
      }

      if (!loaded) {
        buffered = [...buffered, ...change.events];
        return;
      }

      setEvents((current) => appendToLog(current ?? [], change.events));
    });

    allEvents().then(
      (initial) => {
        if (cancelled || discardInitial) return;
        loaded = true;
        setEvents(buffered.length === 0 ? initial : appendToLog(initial, buffered));
        buffered = [];
      },
      (reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason : new Error(String(reason)));
      },
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return { events, error };
}

/**
 * Our own writes are always newer than everything we hold, so the common case
 * is a concat. Only a message from another tab can arrive out of order, and
 * that falls back to union-by-id — the same merge two devices use.
 *
 * Exported so it can be tested directly; nothing else should call it.
 */
export function appendToLog(current: readonly AppEvent[], incoming: readonly AppEvent[]): AppEvent[] {
  const last = current.at(-1);
  const isNewer = last === undefined || incoming.every((event) => event.id > last.id);
  return isNewer ? [...current, ...incoming] : mergeEvents(current, incoming);
}
